import { supabase } from "@/lib/supabaseClient";
import type { PayrollPreflight } from "@shared/types/payroll";
import type {
  CompanyPayrollPeriod,
  PreflightAffectedEmployeeBlocker,
  RpcResult,
} from "./mappers";
import { assertOk } from "./mappers";

export type PayrollSubmitReadinessBlockerSource =
  | "employee"
  | "period"
  | "workflow"
  | "preparation"
  | "system";

export type PayrollSubmitReadinessBlockerResolutionType =
  | "direct_action"
  | "escalation"
  | "informational";

export type PayrollSubmitReadinessBlockerActorScope =
  | "authenticated_user"
  | "owner"
  | "issuer"
  | "system"
  | "external";

export type PayrollSubmitReadinessBlocker = {
  code: string;
  message: string;
  severity: "blocking" | "warning";
  source: PayrollSubmitReadinessBlockerSource;
  actorScope: PayrollSubmitReadinessBlockerActorScope;
  resolutionType: PayrollSubmitReadinessBlockerResolutionType;
  actionableByAuthenticatedUser: boolean;
  employeeProfileId?: string | null;
  editTargetKey?: string | null;
  ctaLabel?: string | null;
  escalationLabel?: string | null;
};

function resolvePeriodId(period: CompanyPayrollPeriod | string): string {
  return typeof period === "string" ? period : period.id;
}

/**
 * Canonical preflight call — single source of truth.
 * No frontend interpretation.
 */
export async function preflight(
  period: CompanyPayrollPeriod | string
): Promise<PayrollPreflight> {
  const periodId = resolvePeriodId(period);

  const res = (await supabase
    .schema("payroll")
    .rpc("preflight_period", {
      p_period_id: periodId,
    })) as RpcResult<PayrollPreflight>;

  return assertOk(res);
}

/**
 * Centralized extractor for affected employee blockers.
 * This is the ONLY place where the payload shape is read.
 * No transformation of meaning — only safe structural normalization.
 */
export function extractAffectedEmployeeBlockers(
  payload: PayrollPreflight
): PreflightAffectedEmployeeBlocker[] {
  const raw = (payload as any)?.checks?.affected_employee_blockers ?? [];

  if (!Array.isArray(raw)) return [];

  return raw.map((row: any) => ({
    employee_profile_id: String(row.employee_profile_id),
    employee_number_mode:
      row.employee_number_mode == null
        ? null
        : String(row.employee_number_mode),
    can_edit_employee_number: Boolean(row.can_edit_employee_number),
    blockers: Array.isArray(row.blockers)
      ? row.blockers.map((b: any) => ({
          requirement_key: String(b.requirement_key),
          label: b.label == null ? null : String(b.label),
          reason: b.reason == null ? null : String(b.reason),
          editable_by_payroll_clerk: Boolean(b.editable_by_payroll_clerk),
          edit_target_kind:
            b.edit_target_kind == null ? null : String(b.edit_target_kind),
          edit_target_key:
            b.edit_target_key == null ? null : String(b.edit_target_key),
        }))
      : [],
  }));
}

function normalizeSubmitBlockerSeverity(
  value: unknown
): PayrollSubmitReadinessBlocker["severity"] {
  return value === "warning" ? "warning" : "blocking";
}

function normalizeSubmitBlockerSource(
  value: unknown
): PayrollSubmitReadinessBlockerSource {
  if (value === "employee") return "employee";
  if (value === "period") return "period";
  if (value === "workflow") return "workflow";
  if (value === "preparation") return "preparation";
  if (value === "system") return "system";

  return "system";
}

function normalizeSubmitBlockerActorScope(
  value: unknown
): PayrollSubmitReadinessBlockerActorScope {
  if (value === "authenticated_user") return "authenticated_user";
  if (value === "owner") return "owner";
  if (value === "issuer") return "issuer";
  if (value === "system") return "system";
  if (value === "external") return "external";

  return "system";
}

function normalizeSubmitBlockerResolutionType(
  value: unknown
): PayrollSubmitReadinessBlockerResolutionType {
  if (value === "direct_action") return "direct_action";
  if (value === "escalation") return "escalation";
  if (value === "informational") return "informational";

  return "escalation";
}

function normalizeBackendSubmitBlocker(
  row: any
): PayrollSubmitReadinessBlocker | null {
  if (!row || typeof row !== "object") return null;

  const code = row.code == null ? "" : String(row.code).trim();
  const message = row.message == null ? "" : String(row.message).trim();

  if (!code || !message) return null;

  return {
    code,
    message,
    severity: normalizeSubmitBlockerSeverity(row.severity),
    source: normalizeSubmitBlockerSource(row.source),
    actorScope: normalizeSubmitBlockerActorScope(row.actor_scope),
    resolutionType: normalizeSubmitBlockerResolutionType(row.resolution_type),
    actionableByAuthenticatedUser: Boolean(
      row.actionable_by_authenticated_user
    ),
    employeeProfileId:
      row.employee_profile_id == null ? null : String(row.employee_profile_id),
    editTargetKey:
      row.edit_target_key == null ? null : String(row.edit_target_key),
    ctaLabel: row.cta_label == null ? null : String(row.cta_label),
    escalationLabel:
      row.escalation_label == null ? null : String(row.escalation_label),
  };
}

function normalizeBackendSubmitBlockers(
  rows: unknown
): PayrollSubmitReadinessBlocker[] {
  if (!Array.isArray(rows)) return [];

  return rows
    .map(normalizeBackendSubmitBlocker)
    .filter(
      (blocker): blocker is PayrollSubmitReadinessBlocker =>
        blocker !== null
    );
}

export type PayrollSubmitReadinessBlockerBuckets = {
  employee: PayrollSubmitReadinessBlocker[];
  period: PayrollSubmitReadinessBlocker[];
  workflow: PayrollSubmitReadinessBlocker[];
  preparation: PayrollSubmitReadinessBlocker[];
  system: PayrollSubmitReadinessBlocker[];
};

export function extractPayrollSubmitReadinessBlockerBuckets(
  payload: PayrollPreflight | null | undefined
): PayrollSubmitReadinessBlockerBuckets {
  return {
    employee: normalizeBackendSubmitBlockers(
      (payload as any)?.employee_submit_blockers
    ),
    period: normalizeBackendSubmitBlockers(
      (payload as any)?.period_submit_blockers
    ),
    workflow: normalizeBackendSubmitBlockers(
      (payload as any)?.workflow_submit_blockers
    ),
    preparation: normalizeBackendSubmitBlockers(
      (payload as any)?.preparation_submit_blockers
    ),
    system: normalizeBackendSubmitBlockers(
      (payload as any)?.system_submit_blockers
    ),
  };
}

/**
 * Canonical extractor for submit readiness blockers.
 * Backend owns blocker existence, classification, actionability, and escalation.
 * This function only normalizes backend snake_case payload fields to BrowserApp
 * camelCase props.
 */
export function extractPayrollSubmitReadinessBlockers(
  payload: PayrollPreflight | null | undefined
): PayrollSubmitReadinessBlocker[] {
  const raw = (payload as any)?.submit_blockers ?? [];

  if (!Array.isArray(raw)) return [];

  return raw
    .map(normalizeBackendSubmitBlocker)
    .filter((blocker): blocker is PayrollSubmitReadinessBlocker =>
      Boolean(blocker)
    );
}

export function hasBlockingPayrollSubmitReadinessBlockers(
  blockers: PayrollSubmitReadinessBlocker[]
): boolean {
  return blockers.some((blocker) => blocker.severity === "blocking");
}